import { DataItem } from '../../types';

/**
 * Query options for filtering, searching, and sorting data
 */
export interface QueryOptions {
    search?: string;
    filterBy?: string; // Can be comma-separated for multiple categories
    sortBy?: keyof DataItem;
    sortOrder?: 'asc' | 'desc';
}

/**
 * Interface for storage providers (SQL.js with OPFS)
 */
export interface StorageProvider {
    /**
     * Initialize the storage provider
     */
    initialize(): Promise<void>;

    /**
     * Seed the database with sample data
     * @param count Number of items to seed
     * @param days Optional number of days to generate (for day-based seeding)
     */
    seedData(count: number, days?: number): Promise<void>;

    /**
     * Get paginated items from storage
     * @param page Page number (1-based)
     * @param pageSize Number of items per page
     * @param options Optional filters, search, and sorting
     * @returns Array of data items
     */
    getItems(page: number, pageSize: number, options?: QueryOptions): Promise<DataItem[]>;

    /**
     * Get total count of items in storage
     * @param options Optional filters and search to count filtered results
     * @returns Total item count
     */
    getTotalItemCount(options?: QueryOptions): Promise<number>;

    /**
     * Get unique categories for filtering
     * @returns Array of unique category names
     */
    getCategories(): Promise<string[]>;

    /**
     * Clear all data from storage
     */
    clearData(): Promise<void>;

    /**
     * Add a new item to storage
     * @param item Item to add (without id, will be auto-generated)
     * @returns The added item with generated id
     */
    addItem(item: Omit<DataItem, 'id'>): Promise<DataItem>;

    /**
     * Update an existing item in storage
     * @param id The id of the item to update
     * @param item The updated item data
     * @returns The updated item
     */
    updateItem(id: string, item: Omit<DataItem, 'id'>): Promise<DataItem>;

    /**
     * Delete an item from storage
     * @param id The id of the item to delete
     */
    deleteItem(id: string): Promise<void>;

    /**
     * Get storage provider name for identification
     */
    getName(): string;

    /**
     * Get storage size/memory usage if available
     * @returns Storage info or null if not available
     */
    getStorageInfo(): Promise<{ used: number; available?: number } | null>;
}

/**
 * Configuration for storage providers
 */
export interface StorageProviderConfig {
    dbName?: string;
    storeName?: string;
    version?: number;
}

/**
 * Factory function type for creating storage providers
 */
export type StorageProviderFactory = (config?: StorageProviderConfig) => StorageProvider;